import React, {
	useCallback,
	useRef,
	useEffect,
	useState,
	type ElementRef,
	type ComponentType,
	type SyntheticEvent,
	type MouseEvent,
	useMemo,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Icon from '@atlaskit/icon';
import EditorRemoveIcon from '@atlaskit/icon/core/migration/delete--editor-remove';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { Box, xcss, Text, Inline } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { SUMMARY_TYPE as SUMMARY_FIELD_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { AsyncFormPreview } from '@atlassian/jira-proforma-request-type-config/src/ui/form-preview/async.tsx';
import { useSwiftLifecycle } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/swift-lifecycle/index.tsx';
import type { ProformaRequestTypeFormCardConfig } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import {
	type AssociatedField,
	type FieldCategoryWithFields,
	FIELD_LAYOUT_ITEM_TYPE,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import { FieldNameWrapper } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/field-name/styled.tsx';
import { HeightAnimator as FieldHeightAnimator } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/height-animator/index.tsx';
import {
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	IconContainer as FieldIconContainer,
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	Form as FieldForm,
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	ChevronContainer as FieldChevronContainer,
	FieldContentAnimation,
	FieldHeaderContainer,
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	Elevation as FieldElevation,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/styled.tsx';
import { FieldChevron } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-chevron/index.tsx';
import { Consumer as FieldHeightAnimatorConsumer } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/height-animator-context/index.tsx';
import {
	FormRestrictionStatus,
	FormSecurityConfigButton,
	loadRestrictedPrincipalsForTemplateForm,
} from '@atlassian/proforma-form-security';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { AnalyticsProvider } from '@atlassian/proforma-utils/analytics';
import { ANALYTICS_CHANNEL } from '@atlassian/jira-proforma-utils/src/index.tsx';
import FormIcon from './assets/form-icon.tsx';
import { messages } from './messages.tsx';
import { RemoveFormConfirmationDialog } from './remove-form-confirmation/index.tsx';

const scrollIntoView = (el: ElementRef<'div'> | null) => {
	if (!el || !el.scrollIntoView) {
		return;
	}
	try {
		// @ts-expect-error - TS2345 - Argument of type '{ block: "nearest"; behaviour: string; }' is not assignable to parameter of type 'boolean | ScrollIntoViewOptions | undefined'.
		el.scrollIntoView({ block: 'nearest', behaviour: 'smooth' });
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		el.scrollIntoView();
	}
};

export type EmptyCardProps = {
	onClick: (analyticsEvent: UIAnalyticsEvent) => void;
};

export type FormCardViewProps = {
	config: ProformaRequestTypeFormCardConfig;
	category?: FieldCategoryWithFields;
	onRemoveFormClick?: (requestTypeId: number) => void;
	projectKey: string;
	autoExpand?: boolean;
};

export const FormCardView: ComponentType<FormCardViewProps> = (props) => {
	const { config, onRemoveFormClick, autoExpand = false, projectKey } = props;

	const cloudId = useCloudId();
	const intl = useIntl();
	const { formatList, formatMessage } = intl;
	const { openCardKeys, cardToggled } = useSwiftLifecycle();
	const isOpen = openCardKeys.includes('formcard');
	const onToggle = () => cardToggled('formcard');

	const onClick = (e: SyntheticEvent) => {
		e.stopPropagation();
		onToggle();
	};

	const handleSubmit = (e: Event) => {
		e.preventDefault();
		onToggle();
	};

	const previouslyOpen = useRef(false);
	const elevationRef = useRef<HTMLDivElement | null>(null);
	const handleElevationRefChanged = useCallback(
		(ref: HTMLDivElement) => {
			elevationRef.current = ref;
		},
		[elevationRef],
	);

	useEffect(() => {
		if (autoExpand) {
			cardToggled('formcard');
		}
	}, [autoExpand, cardToggled]);

	useEffect(() => {
		if (isOpen && !previouslyOpen.current) {
			scrollIntoView(elevationRef.current);
		}
		previouslyOpen.current = isOpen;
	}, [isOpen, previouslyOpen, elevationRef]);

	const { projectId, formName, formId, requestTypeId, linkedFields, restrictionStatus } = config;

	const [showConfirmation, setShowConfirmation] = useState(false);

	const onRemoveFormClickCallback = useCallback(
		// @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
		() => onRemoveFormClick(requestTypeId),
		[onRemoveFormClick, requestTypeId],
	);

	const toggleConfirmation = () => {
		setShowConfirmation(!showConfirmation);
	};

	const mismatchedFields: {
		conditionalFields: AssociatedField[];
		requiredFields: AssociatedField[];
		hasMismatch: boolean;
	} = useMemo(() => {
		if (props.category && linkedFields) {
			// Get required field on request type
			const requiredFieldsOnRequest = props.category.layoutItems
				.filter((item) => item.layoutItemType === FIELD_LAYOUT_ITEM_TYPE && item.data.required) // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				.map((item) => item.data as AssociatedField);

			// Get conditional fields from associated form
			const conditionalFieldsOnForm = Object.keys(linkedFields).filter(
				(key) => !!linkedFields && !!linkedFields[key].conditional,
			);

			/**
			 * Conditional fields mismatch are those that are required
			 * in RTC configuration and conditional in associated form
			 */
			const conditionalMismatchFields = requiredFieldsOnRequest.filter((requestField) =>
				conditionalFieldsOnForm.includes(requestField.key),
			);

			/**
			 * Get non-required fields from associated form that are not conditional
			 * and exclude summary field, as summary is excluded from required error
			 */
			const nonRequiredFieldsOnForm = Object.keys(linkedFields).filter(
				(key) =>
					key !== SUMMARY_FIELD_TYPE &&
					!!linkedFields &&
					!linkedFields[key].required &&
					!linkedFields[key].conditional,
			);

			/**
			 * Required fields mismatch are those that are required
			 * in RTC configuration and not required in associated form.
			 * See previous comments for the full logic behind required mismatch
			 */
			const requiredMismatchFields = requiredFieldsOnRequest.filter((requestField) =>
				nonRequiredFieldsOnForm.includes(requestField.key),
			);

			return {
				conditionalFields: conditionalMismatchFields,
				requiredFields: requiredMismatchFields,
				hasMismatch: conditionalMismatchFields.length > 0 || requiredMismatchFields.length > 0,
			};
		}

		return {
			conditionalFields: [],
			requiredFields: [],
			hasMismatch: false,
		};
	}, [linkedFields, props.category]);

	const getMismatchFieldTooltipMessage = () => {
		if (mismatchedFields.conditionalFields.length > 0) {
			if (mismatchedFields.requiredFields.length > 0) {
				return messages.conditionalAndRequiredFieldMismatchTooltipError;
			}

			return messages.conditionalFieldMismatchTooltipError;
		}

		return messages.requiredFieldMismatchTooltipError;
	};

	const getMismatchFieldSectionMessage = () => {
		if (mismatchedFields.conditionalFields.length > 0) {
			if (mismatchedFields.requiredFields.length > 0) {
				return messages.conditionalAndRequiredFieldMismatchSectionError;
			}

			return messages.conditionalFieldMismatchSectionError;
		}

		return messages.requiredFieldMismatchSectionError;
	};

	/**
	 * Returns required mismatch fields if there is required and conditional mismatch, or
	 * if there is only required fields mismatch. Otherwise returns conditional mismatch fields.
	 * Function returns required mismatch fields if there is required and conditional mismatch
	 * because in that case we display required mismatch field names only to the user. Otherwise we
	 * display conditional mismatch field names.
	 */
	const getMismatchFieldNames = () =>
		(mismatchedFields.requiredFields.length > 0
			? mismatchedFields.requiredFields
			: mismatchedFields.conditionalFields
		).map((field) => `‘${field.name}‘`);

	const formBuilderUrl = `/jira/servicedesk/projects/${projectKey}/settings/forms/form/${formId}/edit`;
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const onFieldToggleTransitionEnd = () => {};
	const stopPropagation = (e: MouseEvent<HTMLElement>) => e.stopPropagation();

	return (
		<>
			<Margin ref={handleElevationRefChanged}>
				<Expandable
					sortable
					isExpandable
					isHighlighted={false}
					isOpen={isOpen}
					deleteAnimation={false}
				>
					{/* @ts-expect-error - TS2322 - Type '(e: Event) => void' is not assignable to type 'FormEventHandler<HTMLFormElement>'. */}
					<FieldForm onSubmit={handleSubmit}>
						<HeaderContainer>
							<HeaderColumn isFlex onClick={onClick}>
								{restrictionStatus &&
								restrictionStatus === FormRestrictionStatus.RESTRICTED &&
								fg('granular_permissions_on_forms_m1') ? (
									<>
										<Box paddingInlineEnd="space.100">
											<AnalyticsProvider channel={ANALYTICS_CHANNEL}>
												<FormSecurityConfigButton
													restrictionStatus={restrictionStatus ?? FormRestrictionStatus.OPEN}
													loadRestrictedPrincipals={() =>
														loadRestrictedPrincipalsForTemplateForm(cloudId, projectId, formId)
													}
													saveRestrictedPrincipals={() => Promise.resolve()}
													isViewMode
													appearance="subtle"
													spacing="compact"
													viewModeManageRestrictionUrl={
														fg('granular_permissions_on_forms_ga') ? formBuilderUrl : undefined
													}
												/>
											</AnalyticsProvider>
										</Box>
										<Inline alignBlock="center">
											<FieldNameWrapper data-testid="project-settings-product-features-jsm-common.ui.form-panel.form-card.form-name">
												{formName}
											</FieldNameWrapper>
										</Inline>
									</>
								) : (
									<>
										<FieldIconContainer>
											<Icon
												glyph={FormIcon}
												label={formatMessage(messages.formIconAriaLabel)}
												size="medium"
											/>
										</FieldIconContainer>
										<FieldNameWrapper data-testid="project-settings-product-features-jsm-common.ui.form-panel.form-card.form-name">
											{formName}
										</FieldNameWrapper>
									</>
								)}
							</HeaderColumn>
							<HeaderColumn>
								<EditButtonWrapper
									appearance="subtle"
									href={formBuilderUrl}
									target="_blank"
									spacing="compact"
									title={formatMessage(messages.editDropdownItemLabel)}
									iconAfter={
										<ShortcutIcon
											label={formatMessage(messages.opensInAnotherWindowAriaLabel)}
											LEGACY_size="small"
										/>
									}
								>
									<FormattedMessage {...messages.editDropdownItemLabel} />
								</EditButtonWrapper>
								{onRemoveFormClick && (
									<Button
										appearance="subtle"
										onClick={toggleConfirmation}
										title={formatMessage(messages.removeFormLabel)}
										spacing="none"
										iconAfter={
											<EditorRemoveIcon
												label={formatMessage(messages.removeFormLabel)}
												LEGACY_size="medium"
												spacing="spacious"
											/>
										}
									/>
								)}

								<FieldChevronContainer
									isOpen={isOpen}
									onMouseDown={stopPropagation}
									onTransitionEnd={onFieldToggleTransitionEnd}
								>
									{mismatchedFields.hasMismatch && !isOpen ? (
										<Tooltip content={formatMessage(getMismatchFieldTooltipMessage())}>
											<Button
												spacing="none"
												appearance="subtle-link"
												onClick={onClick}
												aria-label={formatMessage(messages.fieldMismatchErrorIcon)}
												iconBefore={
													<ErrorIcon
														spacing="spacious"
														label=""
														color={token('color.icon.danger')}
													/>
												}
											/>
										</Tooltip>
									) : (
										<FieldChevron
											isOpen={isOpen}
											onClick={onClick}
											isExpandable
											error={undefined}
										/>
									)}
								</FieldChevronContainer>
							</HeaderColumn>
						</HeaderContainer>

						<FieldHeightAnimator Animation={FieldContentAnimation} isExpanded={isOpen}>
							<FieldHeightAnimatorConsumer>
								{(onRecalculateAnimatorHeight) =>
									isOpen &&
									formId !== undefined && (
										<>
											{mismatchedFields.hasMismatch && (
												<SectionMessageWrapper>
													<SectionMessage appearance="error">
														<FormattedMessage
															{...getMismatchFieldSectionMessage()}
															values={{
																// @ts-expect-error - Type '(content: string) => JSX.Element' is not assignable to type 'ReactNode'.
																b: (content: string) => <Text weight="bold">{content}</Text>,
																fieldList: formatList(getMismatchFieldNames(), {
																	type: 'conjunction',
																	style: 'long',
																}),
																fieldListCount: getMismatchFieldNames().length,
															}}
														/>
													</SectionMessage>
												</SectionMessageWrapper>
											)}
											<Box xcss={formPreviewWrapperStyles}>
												<AsyncFormPreview
													formId={formId}
													onRecalculateParentHeight={onRecalculateAnimatorHeight}
												/>
											</Box>
										</>
									)
								}
							</FieldHeightAnimatorConsumer>
						</FieldHeightAnimator>
						<RemoveFormConfirmationDialog
							isOpen={showConfirmation}
							onConfirm={onRemoveFormClickCallback}
							onCancel={toggleConfirmation}
						/>
					</FieldForm>
				</Expandable>
			</Margin>
			<FireScreenAnalytics />
		</>
	);
};

export type Props = Omit<FormCardViewProps, 'onRemoveFormClick'> & {
	onRemoveFormFromReqType?: (requestTypeId: number) => void;
};

export const FormCard: ComponentType<Props> = (props) => {
	const formCardProps: FormCardViewProps = {
		...props,
		onRemoveFormClick: props.onRemoveFormFromReqType,
	};

	return (
		<JSErrorBoundary
			id="proformaRtcFormCard"
			packageName="jiraProjectSettingsProductFeaturesJsmCommon"
			teamName="jsd-shield"
		>
			<ContextualAnalyticsData sourceName="proformaRtcFormCard" sourceType={SCREEN}>
				<FormCardView {...formCardProps} />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled(FieldHeaderContainer)({
	cursor: 'pointer',
	padding: '0px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderColumn = styled.div<{
	isFlex?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flex: ({ isFlex }) => (isFlex ? 1 : undefined),
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Margin = styled.div({
	marginTop: token('space.100'),
	marginBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditButtonWrapper = styled(Button)({
	marginRight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Expandable = styled(FieldElevation)({
	backgroundColor: token('color.background.input.hovered'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionMessageWrapper = styled.div({
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.100'),
});

const formPreviewWrapperStyles = xcss({
	padding: 'space.100',
});
